<app-screen-shell
  contentWidth="standard"
  [contentClass]="'player-profile-page player-match-history-page'"
  [title]="headerTitle()"
  [subtitle]="headerSubtitle()"
  [loading]="isContentLoading()"
  loadingTitleKey="players.matchHistoryLoading"
  [hideContentWhileLoading]="true"
  (back)="goBack()">

  @if (!isContentLoading() && (!loading || items.length > 0) && canFilterSharedOnly()) {
    <section class="player-match-history-filter profile-content-width player-detail-content-width" [attr.aria-label]="'players.matchHistoryFilter' | translate">
      <button type="button" [class.is-active]="!sharedOnly" [attr.aria-pressed]="!sharedOnly" [disabled]="loading" (click)="setSharedOnly(false)">
        {{ 'players.matchHistoryAll' | translate }}
      </button>
      <button type="button" [class.is-active]="sharedOnly" [attr.aria-pressed]="sharedOnly" [disabled]="loading" (click)="setSharedOnly(true)">
        {{ 'players.matchHistoryTogether' | translate }}
      </button>
    </section>
  }

  @if (!isContentLoading() && errorMsg) {
    <section class="public-player-profile-state">
      <i [class]="'match.history' | nogoshIcon" aria-hidden="true"></i>
      <h1>{{ forbidden ? ('players.matchHistoryUnavailableTitle' | translate) : ('common.error' | translate) }}</h1>
      <p>{{ errorMsg }}</p>
    </section>
  }

  @if (!isContentLoading() && !errorMsg && items.length > 0) {
    <section class="player-match-history-list profile-content-width player-detail-content-width" [attr.aria-label]="'players.matchHistory' | translate">
      @for (item of items; track item.id) {
        <article class="card player-match-history-row">
          <div class="player-match-history-date" [attr.aria-label]="historyDateTimeLabel(item)">
            <strong>{{ historyDateDay(item) }}</strong>
            <span>{{ historyDateMonthYear(item) }}</span>
          </div>

          <div class="player-match-history-content">
            <header class="player-match-history-summary" [class.player-match-history-summary-no-outcome]="!shouldShowItemOutcome(item)">
              <div class="player-match-history-copy">
                @if (item.playSpaceId) {
                  <a class="player-match-history-location" [routerLink]="['/playspace', false]" [queryParams]="{ playSpaceId: item.playSpaceId }">
                    <span>{{ historyMatchLocation(item) }}</span>
                    <i [class]="'locations.location' | nogoshIcon" aria-hidden="true"></i>
                  </a>
                } @else if (historyLocationMapUrl(item)) {
                  <a class="player-match-history-location" [href]="historyLocationMapUrl(item)" target="_blank" rel="noopener">
                    <span>{{ historyMatchLocation(item) }}</span>
                    <i [class]="'locations.location' | nogoshIcon" aria-hidden="true"></i>
                  </a>
                } @else {
                  <h2>{{ historyMatchLocation(item) }}</h2>
                }
                <p>
                  <span>{{ historyTimeLabel(item) }}</span>
                  <span>{{ historyWeekdayLabel(item) }}</span>
                  <span>{{ historyAttendanceLabel(item) }}</span>
                </p>
              </div>
              @if (shouldShowItemOutcome(item)) {
                <div class="player-match-history-outcome" [ngClass]="'player-match-history-outcome-' + historyItemOutcomeKey(item)">
                  <span>{{ historyItemOutcomeLabel(item) }}</span>
                </div>
              }
            </header>

            @if (item.cancelled === true && item.cancelReason) {
              <p class="event-history-cancel-reason">{{item.cancelReason}}</p>
            }

            @if (hasGameResults(item)) {
              <section class="player-match-history-games" [attr.aria-label]="'events.score' | translate">
                @for (game of item.gameResults; track game.id || game.roundNumber) {
                  <div class="player-match-history-game">
                    <div class="player-match-history-scoreline">
                      <span>{{ resultRoundLabel(game) }}</span>
                      @if (hasScore(game)) {
                        <strong>
                          <i [class]="('player.team' | nogoshIcon) + ' event-history-white-shirt'" aria-hidden="true"></i>
                          <span>{{game.whiteScore}} : {{game.blackScore}}</span>
                          <i [class]="('player.team' | nogoshIcon) + ' event-history-black-shirt'" aria-hidden="true"></i>
                        </strong>
                      }
                      @if (shouldShowRoundOutcome(item)) {
                        <em [ngClass]="hasScore(game) ? ('player-match-history-round-' + historyRoundOutcomeKey(game)) : 'player-match-history-round-no-score'">
                          {{ hasScore(game) ? historyRoundOutcomeLabel(game) : ('players.matchHistoryNoScore' | translate) }}
                        </em>
                      }
                    </div>

                    @if (shouldShowRoster(item, game)) {
                      <div class="player-match-history-roster">
                        <div class="player-match-history-team">
                          @for (participant of gameTeamPlayers(item, game, 'WHITE'); track participant.id) {
                            <div class="player-match-history-player">
                              @if (participant.avatar) {
                                <img [src]="participant.avatar" [alt]="playerDisplayName(participant)" onerror="this.style.display='none'">
                              } @else {
                                <span>{{playerInitials(participant)}}</span>
                              }
                              <b>{{playerDisplayName(participant)}}</b>
                            </div>
                          }
                        </div>
                        <div class="player-match-history-team">
                          @for (participant of gameTeamPlayers(item, game, 'BLACK'); track participant.id) {
                            <div class="player-match-history-player">
                              @if (participant.avatar) {
                                <img [src]="participant.avatar" [alt]="playerDisplayName(participant)" onerror="this.style.display='none'">
                              } @else {
                                <span>{{playerInitials(participant)}}</span>
                              }
                              <b>{{playerDisplayName(participant)}}</b>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </section>
            }
          </div>
        </article>
      }
    </section>
  }

  @if (!isContentLoading() && !loading && !errorMsg && items.length === 0) {
    <p class="event-history-empty">{{ sharedOnly ? ('players.matchHistoryTogetherEmpty' | translate) : ('events.noPlayedMatches' | translate) }}</p>
  }

  @if (!isContentLoading() && !errorMsg && hasMore) {
    <div class="event-history-load-more">
      <button type="button" class="btn btn-secondary btn-sm px-3 text-white" [disabled]="loading" (click)="loadMore()">{{ 'common.more' | translate }}</button>
    </div>
  }
</app-screen-shell>
